import {
  formatManilaDate,
  formatManilaDateTime,
  formatManilaTime,
  toManilaParts,
} from "../../utils/manilaTime";

// All learning queries share this prefix so one invalidation refreshes them.
export const LEARNING_KEY = "me:learning";

export const SEMINAR_TYPE_LABELS = {
  SEMINAR: "Seminar",
  WORKSHOP: "Workshop",
  TRAINING: "Training",
  WEBINAR: "Webinar",
  CONFERENCE: "Conference",
};

export const SEMINAR_MODE = {
  ONSITE: { label: "On-site", icon: "bi-geo-alt" },
  ONLINE: { label: "Online", icon: "bi-camera-video" },
  HYBRID: { label: "Hybrid", icon: "bi-broadcast" },
};

// My attendance at a seminar.
export const PARTICIPANT_STATUS = {
  PENDING: { label: "Registered", tone: "blue" },
  ATTENDED: { label: "Attended", tone: "green" },
  ABSENT: { label: "Absent", tone: "red" },
  EXCUSED: { label: "Excused", tone: "gray" },
};

export const PARTICIPANT_SOURCE_LABELS = {
  ASSIGNED: "Assigned by HR",
  SELF_ENROLLED: "Self-registered",
};

// Seminar state shown on a card: cancelled/completed first, then the phase.
export const getSeminarState = (seminar) => {
  if (seminar.status === "CANCELLED")
    return { label: "Cancelled", tone: "red" };
  if (seminar.status === "COMPLETED")
    return { label: "Completed", tone: "gray" };
  if (seminar.phase === "ONGOING") return { label: "Ongoing", tone: "green" };
  if (seminar.phase === "ENDED") return { label: "Ended", tone: "gray" };
  return { label: "Upcoming", tone: "purple" };
};

// "Oct 20, 2026 · 9:00 AM – 5:00 PM", or both full dates when it spans days.
export const formatSeminarWhen = (startAt, endAt) => {
  if (!startAt) return "—";
  const sameDay =
    endAt && toManilaParts(startAt).dateKey === toManilaParts(endAt).dateKey;
  return sameDay
    ? `${formatManilaDate(startAt)} · ${formatManilaTime(startAt)} – ${formatManilaTime(endAt)}`
    : `${formatManilaDateTime(startAt)} – ${formatManilaDateTime(endAt)}`;
};

/**
 * What My Learning offers for checking in: { kind, text?, when? }.
 * kind: "button" | "qr" | "blocked" | "none" (not on it, or already recorded).
 */
export const selfCheckInView = (seminar, nowMs = Date.now()) => {
  const sc = seminar?.selfCheckIn;
  if (!sc || seminar.my?.status !== "PENDING") return { kind: "none" };
  if (sc.available) return { kind: "button" };
  // Before anything else, even scanning a QR (which is refused until then)
  if (seminar.agreement?.mustAccept)
    return { kind: "blocked", text: "Accept the training agreement first" };
  const open = seminar.status === "SCHEDULED";
  if (sc.usesQr && open && seminar.phase !== "ENDED")
    return {
      kind: "qr",
      text: "This seminar uses QR check-in. Scan the organizer's QR.",
    };
  if (sc.usesQr && open)
    return { kind: "blocked", text: "Check-in closed when the seminar ended" };
  const notYet = sc.opensAt && new Date(sc.opensAt).getTime() > nowMs;
  return {
    kind: "blocked",
    text: sc.blockedReason || "Check-in isn't open.",
    ...(notYet && {
      when: `Opens ${formatManilaDateTime(sc.opensAt)}, closes ${formatManilaDateTime(sc.closesAt)}`,
    }),
  };
};

/** How my attendance was logged, once it is. */
export const myCheckInText = (my) => {
  if (!my || my.status !== "ATTENDED") return "";
  const how =
    my.checkInMethod === "SELF"
      ? "You checked in yourself."
      : my.checkInMethod === "QR"
        ? "You checked in with the QR."
        : "Logged by the organizer.";
  return my.locationVerified === false
    ? `${how} It was recorded and flagged for HR to review, because your location couldn't be confirmed at the venue.`
    : how;
};

// Hours come back as numbers or decimal strings; null when not set.
export const formatLearningHours = (hours) => {
  if (hours == null || hours === "") return "—";
  const value = Number(hours);
  return `${Number.isInteger(value) ? value : value.toFixed(1)} h`;
};

export const getYearOptions = () => {
  const current = new Date().getFullYear();
  return [current + 1, current, current - 1, current - 2, current - 3];
};
